import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BackToTop from '../../components/BackToTop/BackToTop';
import './Subsidiaries.css';

const Subsidiaries = () => {
  const [counters, setCounters] = useState({
    years: 0,
    partners: 0,
    subsidiaries: 0
  });

  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      const targets = {
        years: 48,
        partners: 100,
        subsidiaries: 11
      };

      let currentStep = 0;

      const counterInterval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          years: Math.floor(progress * targets.years),
          partners: Math.floor(progress * targets.partners),
          subsidiaries: Math.floor(progress * targets.subsidiaries)
        });

        if (currentStep >= steps) {
          clearInterval(counterInterval);
          setCounters(targets);
        }
      }, stepDuration);

      return () => clearInterval(counterInterval);
    };

    // Start counter animation when component mounts
    animateCounters();
  }, []);

  const subsidiaries = [
    {
      tag: "Parent Company",
      icon: "fas fa-building",
      title: "Trimline Group",
      description: "Parent company of all subsidiaries, focusing on global trade, supply chain, and international business solutions.",
      link: "https://www.trimlineco.com"
    },
    {
      tag: "International Trade",
      icon: "fas fa-globe",
      title: "Trimline International LLC",
      description: "Handles overseas operations and business expansion across major global hubs.",
      link: "https://www.trimlineco.com"
    },
    {
      tag: "International Trade",
      icon: "fas fa-industry",
      title: "Trimline Polymers",
      description: "Specialized in supply of industrial polymers and raw materials for packaging and manufacturing industries.",
      link: "https://trimlinepolymers.com/"
    },
    {
      tag: "E-Commerce",
      icon: "fas fa-shopping-cart",
      title: "Khazanay TL Enterprises",
      description: "Focused on trading and investment services to strengthen diversified business operations.",
      link: "https://www.khazanay.pk/"
    },
    {
      tag: "International Trade",
      icon: "fas fa-box",
      title: "Trimline Commodities",
      description: "Engaged in international commodity trading and raw material supply chain management.",
      link: "https://www.trimlineco.com"
    },
    {
      tag: "Chemicals",
      icon: "fas fa-flask",
      title: "Trimline Jade Chemicals",
      description: "Suppliers of premium chemicals for industrial and commercial applications worldwide.",
      link: "https://jadechemi.com/"
    },
    {
      tag: "E-Commerce",
      icon: "fas fa-tag",
      title: "Trimline Brands Co",
      description: "Focused on e-commerce, retail, and developing premium brand experiences for consumers.",
      link: "/brands"
    },
    {
      tag: "Technology",
      icon: "fas fa-laptop-code",
      title: "Trimline Tech Ventures",
      description: "Dedicated to technology solutions, innovation, and global digital transformation projects.",
      link: "/tech"
    },
    {
      tag: "Import/Export",
      icon: "fas fa-ship",
      title: "Trimline Impex",
      description: "International import-export division ensuring smooth trade operations across multiple industries.",
      link: "#"
    },
    {
      tag: "Holdings",
      icon: "fas fa-chart-line",
      title: "JYT Holdings",
      description: "Investment and holding company diversifying Trimline's business interests globally.",
      link: "https://jytholdings.com/"
    },
    {
      tag: "Communications",
      icon: "fas fa-bullhorn",
      title: "Trimline Communication",
      description: "Specialized in media, corporate communications, and marketing consultancy services.",
      link: "https://www.trimlineco.com"
    }
  ];

  return (
    <div className="subsidiaries-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <video className="hero-video" autoPlay muted loop playsInline>
            <source src="/video/video.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Our Subsidiaries</h1>
            <p className="hero-subtitle">Discover the diverse business units working together under the Trimline Group umbrella to deliver excellence across multiple industries</p>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">{counters.years}</span>
                <span className="stat-label">Years of Excellence</span>
              </div>
              <div className="stat">
                <span className="stat-number">{counters.partners}+</span>
                <span className="stat-label">Partners Worldwide</span>
              </div>
              <div className="stat">
                <span className="stat-number">{counters.subsidiaries}</span>
                <span className="stat-label">Subsidiaries</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Structure Visualization */}
      <section className="corporate-structure">
        <div className="container">
          <div className="structure-header">
            <h2>Corporate Structure</h2>
            <p className="structure-description">
              A system that outlines how certain activities are directed in order to achieve the goals of an organization.
            </p>
          </div>
          
          <div className="mountain-structure">
            {/* Level 1 - Parent Company */}
            <div className="mountain-level parent-level">
              <div className="company-card parent">
                <h3>Trimline Group</h3>
                <p>Parent Company</p>
              </div>
            </div>

            {/* Level 2 - International */}
            <div className="mountain-level international-level">
              <div className="level-label">International Operations</div>
              <div className="companies-row">
                <div className="company-card international">
                  <h4>Trimline International LLC</h4>
                  <p>Global Operations</p>
                </div>
              </div>
            </div>

            {/* Level 3 - Business Units */}
            <div className="mountain-level business-level">
              <div className="level-label">Business Units</div>
              <div className="companies-row">
                <div className="company-card business">
                  <h4>Trimline Jade Chemicals</h4>
                  <p>Chemical Supply</p>
                </div>
                <div className="company-card business">
                  <h4>Trimline Commodities</h4>
                  <p>Commodity Trading</p>
                </div>
                <div className="company-card business">
                  <h4>Trimline Polymers</h4>
                  <p>Polymer Supply</p>
                </div>
                <div className="company-card business">
                  <h4>Trimline Tech Ventures</h4>
                  <p>Technology Solutions</p>
                </div>
              </div>
            </div>

            {/* Level 4 - Operations */}
            <div className="mountain-level operations-level">
              <div className="level-label">Core Operations</div>
              <div className="companies-row">
                <div className="company-card operations">
                  <h4>Trimline Impex</h4>
                  <p>Import/Export</p>
                </div>
                <div className="company-card operations">
                  <h4>Trimline Brands Co</h4>
                  <p>E-commerce & Retail</p>
                </div>
                <div className="company-card operations">
                  <h4>Khazanay TL</h4>
                  <p>Trading & Investment</p>
                </div>
              </div>
            </div>

            {/* Level 5 - Foundation */}
            <div className="mountain-level foundation-level">
              <div className="level-label">Foundation & Support</div>
              <div className="companies-row">
                <div className="company-card support">
                  <h4>Trimline Communication</h4>
                  <p>Corporate Communications</p>
                </div>
                <div className="company-card support">
                  <h4>JYT Holdings</h4>
                  <p>Investment & Holdings</p>
                </div>
              </div>
            </div>

            {/* Connecting Lines */}
            <div className="structure-connectors">
              <div className="connector vertical main-connector"></div>
              <div className="connector horizontal international-connector"></div>
              <div className="connector horizontal business-connector"></div>
              <div className="connector horizontal operations-connector"></div>
              <div className="connector horizontal foundation-connector"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Subsidiaries Grid */}
      <section className="subsidiaries-container">
        <div className="container">
          <div className="subsidiaries-grid">
            {subsidiaries.map((subsidiary, index) => (
              <div className="subsidiary-card" key={index}>
                <div className="subsidiary-tag">{subsidiary.tag}</div>
                <div className="subsidiary-card-header">
                  <div className="subsidiary-icon">
                    <i className={subsidiary.icon}></i>
                  </div>
                  <h2>{subsidiary.title}</h2>
                </div>
                <div className="subsidiary-card-content">
                  <p>{subsidiary.description}</p>
                </div>
                <div className="subsidiary-card-footer">
                  {subsidiary.link.startsWith('http') ? (
                    <a href={subsidiary.link} target="_blank" rel="noopener noreferrer" className="btn">
                      Explore <i className="fas fa-arrow-right"></i>
                    </a>
                  ) : (
                    <Link to={subsidiary.link} className="btn">
                      Explore <i className="fas fa-arrow-right"></i>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BackToTop />
    </div>
  );
};

export default Subsidiaries;